import { useEffect } from 'react';
import { Users, Zap, Sparkles, CheckCircle2, ArrowRight, TrendingUp } from 'lucide-react';
import SignupForm from '../../components/auth/SignupForm';
import type { ReferralSignupOffer } from '../../lib/referralPartnerOffer';
import { setCurrentSignupFlow, trackSignupEvent, trackSignupEventOnce } from '../../lib/signupAnalytics';

interface ReferralLandingProps {
  /** Present when the visitor arrived via a referral partner link; omitted for standard signup. */
  offer?: ReferralSignupOffer | null;
}

const PROPOSAL_FEATURES = [
  'Send professional proposals in minutes',
  'Custom branded templates for every service',
  'Client e-signature & instant approval',
  'Auto-convert accepted proposals to bookings',
  'Track open, viewed, and signed status in real time',
  '100% managed follow-up reminders',
  'Turn Google Earth measurements into instant prices',
];

const TEAM_FEATURES = [
  'Track team members in real time',
  'See booking activity as it happens',
  'See trip data and KM traveled',
  'Auto track time on the road and spent on jobs',
  'Track team hours, bonuses, commissions and tips',
  'Team efficiency reports',
];

const INTEGRATIONS = [
  { name: 'Google Calendar', cat: 'Scheduling', color: '#4285F4' },
  { name: 'Stripe', cat: 'Payments', color: '#635BFF' },
  { name: 'QuickBooks', cat: 'Accounting', color: '#2CA01C' },
  { name: 'Google Maps', cat: 'Routing', color: '#EA4335' },
  { name: 'Twilio', cat: 'SMS & Calls', color: '#F22F46' },
  { name: 'Gmail', cat: 'Email', color: '#EA4335' },
  { name: 'Zapier', cat: 'Automation', color: '#FF4F00' },
  { name: 'Xero', cat: 'Accounting', color: '#13B5EA' },
  { name: 'Google Analytics', cat: 'Analytics', color: '#E8710A' },
  { name: 'PayPal', cat: 'Payments', color: '#003087' },
  { name: 'Slack', cat: 'Team Comms', color: '#611f69' },
  { name: 'Google Business', cat: 'Reviews & SEO', color: '#4285F4' },
];

// Real app-icon PNGs (same assets used as the in-app launcher icons).
const HUB_APP_ICONS = [
  '/CRMappicon.png', '/Calenderappicon.png', '/Fieldproappicon.png', '/Customersappicon.png', '/Messagesappicon.png',
  '/Proposalsappicon.png', '/Invoicesappicon.png', '/Inventoryappicon.png', '/Expensesappicon.png', '/Timesheetsappicon.png',
  '/Teamsappicon.png', '/Marketingappicon.png', '/Analyticsappicon.png', '/Automateappicon.png', '/Servicesappicon.png',
  '/Giftcardsappicon.png', '/Storeappicon.png', '/Bookingsappicon.png', '/Projectsappicon.png', '/tasksappicon.png',
];

const HUB_BENEFITS = [
  { icon: CheckCircle2, title: 'All-in-One Platform', desc: '21 purpose-built apps including CRM, scheduling, payments, analytics, and more — everything under one roof.' },
  { icon: Zap, title: 'Seamless Integrations', desc: 'Connect with Google Calendar, Stripe, QuickBooks, and hundreds of tools you already use.' },
  { icon: Users, title: 'Built for Teams', desc: 'Collaborate effortlessly with your team, from field workers to management.' },
  { icon: TrendingUp, title: 'Scales With You', desc: 'From solo entrepreneur to enterprise — Nexus grows alongside your business.' },
];

export function GradientText({ children }: { children: React.ReactNode }) {
  return (
    <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">{children}</span>
  );
}

export function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-white/[0.04] border border-cyan-500/30 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-cyan-300">
      {children}
    </span>
  );
}

/** Landing-page ambient background: pure black + top cyan radial glow. */
export function LandingAmbientGlow() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[500px] rounded-full blur-[140px] opacity-25"
        style={{ background: 'radial-gradient(circle, rgba(34,211,238,0.5), rgba(59,130,246,0.2) 60%, transparent 70%)' }}
      />
    </div>
  );
}

function Check({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-emerald-50 border border-emerald-200">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
      </span>
      <span className="text-[15px] text-gray-700 leading-relaxed">{text}</span>
    </li>
  );
}

/** Decorative on-brand "app preview" panel used opposite each feature section. */
function PreviewPanel({ accent = '#22d3ee', rows = 3 }: { accent?: string; rows?: number }) {
  return (
    <div className="relative rounded-2xl border border-gray-200 bg-white p-3 shadow-[0_8px_30px_rgba(0,0,0,0.08)] overflow-hidden">
      <div
        className="absolute -top-24 -right-16 w-64 h-64 rounded-full blur-3xl opacity-15 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${accent}, transparent 70%)` }}
      />
      {/* Window chrome */}
      <div className="flex items-center gap-1.5 px-2 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-yellow-400/70" />
        <span className="h-2.5 w-2.5 rounded-full bg-green-400/70" />
        <div className="ml-3 h-5 flex-1 rounded-md bg-gray-100" />
      </div>
      {/* Faux content */}
      <div className="relative p-2 space-y-2.5">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg flex-shrink-0" style={{ background: `linear-gradient(135deg, ${accent}40, #3b82f640)` }} />
              <div className="flex-1 space-y-1.5">
                <div className="h-2.5 rounded bg-gray-200" style={{ width: `${70 - i * 12}%` }} />
                <div className="h-2 rounded bg-gray-100" style={{ width: `${45 + i * 8}%` }} />
              </div>
              <div className="h-6 w-14 rounded-md" style={{ background: `${accent}26` }} />
            </div>
          </div>
        ))}
      </div>
      {/* Floating accent badge */}
      <div className="absolute bottom-4 right-4 rounded-xl bg-white border border-gray-200 shadow-md px-3 py-2 flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4" style={{ color: accent }} />
        <span className="text-[12px] font-semibold text-gray-900">All in sync</span>
      </div>
    </div>
  );
}

const PrimaryCTA = ({ onClick, children }: { onClick: () => void; children: React.ReactNode }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 rounded-full bg-gray-900 hover:bg-black px-7 py-3.5 text-[15px] font-semibold text-white shadow-sm transition-all active:scale-[0.98]"
  >
    {children} <ArrowRight className="h-4 w-4 text-emerald-300" />
  </button>
);

/**
 * Conversion-focused signup landing, styled to match the Nexus marketing site
 * (pure-black, cyan→blue gradient, pill badges, feature checklists, integrations,
 * one-hub grid). Used for BOTH standard signup and referral-partner arrivals —
 * the partner badge and discount copy render only when an offer is present.
 */
export default function ReferralLanding({ offer }: ReferralLandingProps) {
  const hasDiscount = !!(offer?.signup_discount_enabled && offer?.signup_discount_percent);
  // The marketing sections below the signup hero only show on the partner
  // (referral) flow; the standard signup stays focused on the form.
  const isPartnerFlow = !!offer;
  const scrollToSignup = (ctaLabel?: string) => {
    if (ctaLabel) {
      trackSignupEvent({
        eventType: 'click',
        step: 'landing',
        data: { cta: ctaLabel },
        referralCode: offer?.code ?? null,
      });
    }
    document.getElementById('referral-signup')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Funnel top: landing page view + the account form being on screen.
  useEffect(() => {
    setCurrentSignupFlow('standard');
    trackSignupEventOnce('page_view:landing', {
      eventType: 'page_view',
      step: 'landing',
      data: { partner_flow: isPartnerFlow },
      referralCode: offer?.code ?? null,
    });
    trackSignupEventOnce('step_view:account', {
      eventType: 'step_view',
      step: 'account',
      referralCode: offer?.code ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen text-gray-900 relative overflow-x-hidden bg-white"
      style={{
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top bar */}
        <div className="flex items-center justify-center py-5">
          <div className="text-xl font-extrabold tracking-tight text-gray-900">NexusAI</div>
        </div>

        {/* ── Hero ───────────────────────────────────────────── */}
        {/* Partner flow: marketing hero text + form side by side. Standard
            signup: just the form, centered — no marketing copy. */}
        <section
          className={
            isPartnerFlow
              ? 'grid lg:grid-cols-2 gap-8 lg:gap-12 items-center pt-2 lg:pt-6 pb-12 lg:pb-14'
              : 'flex justify-center pt-2 lg:pt-6 pb-12 lg:pb-14'
          }
        >
          {isPartnerFlow && (
            <div className="order-2 lg:order-1">
              <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 border border-gray-200 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-600"><Sparkles className="h-3.5 w-3.5 text-emerald-500" /> The Ultimate Service Company Builder</span>

              <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[56px] font-extrabold leading-[1.03] tracking-tight">
                Scale Your <span className="text-blue-600">Business</span> Faster and Smarter
              </h1>

              <p className="mt-5 text-lg text-gray-600 max-w-xl">
                One platform to run your entire operation. CRM, scheduling, field management,
                marketing, and AI — all connected, all in one place.
              </p>

              {offer?.partner_name && (
                <div className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-[12px] font-semibold text-emerald-700">
                  <Sparkles className="h-3.5 w-3.5" />
                  {offer.partner_name} invited you to Nexus
                  {hasDiscount ? ` · ${offer.signup_discount_percent}% off your first month` : ''}
                </div>
              )}

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <PrimaryCTA onClick={() => scrollToSignup('hero_start_free_trial')}>Start Free Trial</PrimaryCTA>
                <span className="text-sm text-gray-500">No commitment — cancel anytime.</span>
              </div>
            </div>
          )}

          {/* Signup form (scroll target) — first on mobile, right column on desktop */}
          <div
            id="referral-signup"
            className={isPartnerFlow ? 'order-1 lg:order-2 flex justify-center lg:justify-end' : 'w-full flex justify-center'}
          >
            <SignupForm referralOffer={offer} />
          </div>
        </section>

        {/* ── Marketing sections — partner (referral) signup flow only ── */}
        {isPartnerFlow && (
        <>
        {/* Feature pills row */}
        <div className="flex flex-wrap justify-center gap-2.5 pb-16">
          {['NexusAI', 'CRM', 'Customer Hub', 'Field Hub', 'Marketing Hub', 'Team App', 'Scheduling', 'Payments', 'Proposals'].map((p) => (
            <span key={p} className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 border border-gray-200 px-3.5 py-1.5 text-[12px] font-medium text-gray-600">
              {p} <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
            </span>
          ))}
        </div>

        {/* ── Smart Proposals (visual left / text right) ──────── */}
        <section className="py-14 border-t border-gray-100 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="order-2 lg:order-1">
            <PreviewPanel accent="#22d3ee" rows={3} />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Win More Jobs With <span className="text-blue-600">Smart Proposals</span>
            </h2>
            <p className="mt-4 text-gray-600">
              Close deals faster with beautiful, branded proposals your clients can sign instantly — from any device.
            </p>
            <ul className="mt-7 space-y-3.5">
              {PROPOSAL_FEATURES.map((f) => <Check key={f} text={f} />)}
            </ul>
            <div className="mt-8"><PrimaryCTA onClick={() => scrollToSignup('proposals_cta')}>Start Sending Proposals</PrimaryCTA></div>
          </div>
        </section>

        {/* ── Team Intelligence (text left / visual right) ────── */}
        <section className="py-14 border-t border-gray-100 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 border border-gray-200 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-600"><Users className="h-3.5 w-3.5 text-emerald-500" /> Team Intelligence</span>
            <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight">
              See More <span className="text-blue-600">Staff Data</span>
            </h2>
            <p className="mt-4 text-gray-600">
              Complete visibility into your team's performance — from the road to the job site, in real time.
            </p>
            <ul className="mt-7 space-y-3.5">
              {TEAM_FEATURES.map((f) => <Check key={f} text={f} />)}
            </ul>
            <div className="mt-8"><PrimaryCTA onClick={() => scrollToSignup('team_cta')}>Get Full Team Visibility</PrimaryCTA></div>
          </div>
          <div>
            <PreviewPanel accent="#3b82f6" rows={3} />
          </div>
        </section>

        {/* ── Integrations ────────────────────────────────────── */}
        <section className="py-12 border-t border-gray-100 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-gray-50 border border-gray-200 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-gray-600">Integrations</span>
          <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight">
            Connects With the Tools <span className="text-blue-600">You Already Use</span>
          </h2>
          <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
            Nexus plugs into your existing stack — no migration headaches, no switching costs. Just everything working together.
          </p>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {INTEGRATIONS.map((it) => (
              <div key={it.name} className="rounded-2xl bg-white border border-gray-200 p-4 flex flex-col items-center gap-2">
                <div className="h-9 w-9 rounded-xl flex items-center justify-center text-white text-sm font-bold" style={{ background: it.color }}>
                  {it.name.charAt(0)}
                </div>
                <div className="text-[12px] font-semibold text-gray-900 leading-tight">{it.name}</div>
                <div className="text-[10px] text-gray-500">{it.cat}</div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-xs text-gray-400 tracking-widest">— and more —</div>
        </section>

        {/* ── One Hub for Everything ──────────────────────────── */}
        <section className="py-12 border-t border-gray-100 grid lg:grid-cols-2 gap-10 items-center">
          <div className="grid grid-cols-5 gap-3">
            {HUB_APP_ICONS.map((src) => (
              <img key={src} src={src} alt="" className="aspect-square w-full rounded-xl shadow-lg object-cover" />
            ))}
          </div>
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              One Hub for <span className="text-blue-600">Everything</span>
            </h2>
            <p className="mt-4 text-gray-600">
              Stop juggling multiple tools. Nexus brings everything you need into one powerful platform — plus seamless integrations with the tools you already love.
            </p>
            <div className="mt-7 space-y-5">
              {HUB_BENEFITS.map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.title} className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-50 border border-emerald-200">
                      <Icon className="h-4 w-4 text-emerald-600" />
                    </span>
                    <div>
                      <div className="font-semibold text-gray-900">{b.title}</div>
                      <div className="text-sm text-gray-600 leading-relaxed">{b.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ── Closing CTA ─────────────────────────────────────── */}
        <section className="py-16 text-center border-t border-gray-100">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
            Ready to <span className="text-blue-600">Scale</span>?
          </h2>
          <p className="mt-3 text-gray-600">
            {hasDiscount
              ? `Claim your ${offer?.signup_discount_percent}% first-month discount and start today.`
              : 'Run your entire operation from one platform — start today.'}
          </p>
          <div className="mt-7 flex justify-center"><PrimaryCTA onClick={() => scrollToSignup('closing_cta')}>Create Your Account</PrimaryCTA></div>
        </section>
        </>
        )}

        <footer className="py-8 text-center text-xs text-gray-400">
          © {new Date().getFullYear()} NexusAI. All rights reserved.
        </footer>
      </div>
    </div>
  );
}
